// globalSearchApi.ts — client for the tenant global-search backend.
//
// Endpoint contract (not the old faceted /tenant/search used by
// features/search's SearchBar/SearchPage — that one is workflow-only and out
// of scope here):
//
//   GET /tenant/search/global?q=<2..100 chars>&all_history=true|false
//   200 { groups: [{ type, count, truncated, items: [{ id, type, title,
//                     subtitle, url, highlights: [{ field, value }] }] }] }
//   400 on a malformed q. Groups the caller's role can't see are simply
//   ABSENT from the response — never assume all six types are present.
//   Cache-Control: no-store (set by the backend; the fetch client already
//   passes cache:'no-store' for every request, see services/api/client.ts).
//   Rate-limited 20/2s per user — a 429 is a normal, expected response under
//   fast typing, not a failure; callers should keep showing the previous
//   result set rather than erroring (see useGlobalSearch.ts).
//
// Uses the same apiClient singleton (@/services/api/client) as every other
// tenant-scoped call, so the existing session/auth/tenant-header handling in
// ApiClient.request() applies unchanged.

import { apiClient } from '@/services/api/client'

// Type strings are a literal contract with the backend — internal/api/v1/
// search/handler.go's fetchGroup(...) calls (workflow, workflow_engine_ref,
// execution, user, audit_event, tenant, agent_definition, agent_skill,
// team, workflow_chain, runbook, stategraph_definition, workflow_template)
// and search_repository.go's Type: field literals are the source of truth.
// These are NOT free-form frontend choices; a mismatch here means that
// group's CommandPalette.tsx TYPE_ORDER lookup
// (`searchGroups.find((g) => g.type === type)`) silently never matches and
// the whole group never renders — see CommandPalette.test.tsx's "renders
// every canonical backend group type" contract-pin test (pinned to the
// original six; the seven Build-hub types added afterward are covered by a
// separate test).
export type GlobalSearchResultType =
  | 'workflow'
  | 'workflow_engine_ref'
  | 'execution'
  | 'user'
  | 'audit_event'
  | 'tenant'
  | 'agent_definition'
  | 'agent_skill'
  | 'team'
  | 'workflow_chain'
  | 'runbook'
  | 'stategraph_definition'
  | 'workflow_template'

export interface GlobalSearchHighlight {
  field: string
  value: string
}

export interface GlobalSearchItem {
  id: string
  type: GlobalSearchResultType
  title: string
  subtitle?: string
  url: string
  highlights?: GlobalSearchHighlight[]
}

export interface GlobalSearchGroup {
  type: GlobalSearchResultType
  count: number
  truncated: boolean
  items: GlobalSearchItem[]
}

export interface GlobalSearchResponse {
  groups: GlobalSearchGroup[]
}

export interface GlobalSearchOptions {
  allHistory?: boolean
  signal?: AbortSignal
}

export const globalSearchApi = {
  search(q: string, opts: GlobalSearchOptions = {}): Promise<GlobalSearchResponse> {
    const params: Record<string, unknown> = { q }
    if (opts.allHistory) {
      params.all_history = 'true'
    }
    return apiClient.get<GlobalSearchResponse>('/tenant/search/global', params, {
      signal: opts.signal,
    })
  },
}
